import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { myExpariencesService } from "../../../services";
import clsx from "clsx";
import DndKit from "../../../components/dragNDrop/DndKit";
import { useViewport } from "../../../helpers";

const PREFIX = "VisibilityAndOrder";

const classes = {
  switchBase: `${PREFIX}-switchBase`,
  checked: `${PREFIX}-checked`,
  track: `${PREFIX}-track`,
  root: `${PREFIX}-root`,
  selectContainer: `${PREFIX}-selectContainer`,
  visibilityLine: `${PREFIX}-visibilityLine`,
  imageExparienceDropdown: `${PREFIX}-imageExparienceDropdown`,
  selectTextVisibility: `${PREFIX}-selectTextVisibility`,
  visibleSwitch: `${PREFIX}-visibleSwitch`,
  titleStyle: `${PREFIX}-titleStyle`,
  inactiveContainer: `${PREFIX}-inactiveContainer`,
  titlesMargin: `${PREFIX}-titlesMargin`,
  inactiveFrame: `${PREFIX}-inactiveFrame`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.selectContainer}`]: {
    display: "flex",
    flexDirection: "row",
    "& > img": {
      textAlign: "left",
      marginRight: "1rem",
      objectFit: "contain",
    },
    "& > div": {
      textAlign: "left",
      marginRight: "1rem",
    },
  },

  [`& .${classes.visibilityLine}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  [`& .${classes.imageExparienceDropdown}`]: {
    width: "60px",
    height: "60px",
    objectFit: "contain",
  },

  [`& .${classes.selectTextVisibility}`]: {
    marginRight: "3rem",
    height: "30px",
    transform: "translateY(5px)",
    width: "95px",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },

  [`& .${classes.visibleSwitch}`]: {
    width: "100px",
  },

  [`& .${classes.titleStyle}`]: {
    marginLeft: "1rem !important",
    maxWidth: "62px",
  },

  [`& .${classes.inactiveContainer}`]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    maxWidth: "450px",
    margin: "0 auto",
  },

  [`& .${classes.titlesMargin}`]: {
    margin: "1rem 0 !important",
    textAlign: "center",
  },

  [`& .${classes.inactiveFrame}`]: {
    height: "70px",
    display: "flex",
    [theme.breakpoints.between("xs", "md")]: {
      maxWidth: "350px",
    },
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "4px",
    margin: "0.25rem 0",
    padding: "0.25rem",
  },
}));

const GoldenSwitch = Switch;

const VisibilityAndOrder = (props) => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const { enqueueSnackbar } = useSnackbar();
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
  const [activeItems, setActiveItems] = useState(null);
  const [inactiveItems, setInactiveItems] = useState(null);

  useEffect(() => {
    if (props.frames && !activeItems && !inactiveItems) {
      let tmp = props.frames.filter((elem) => elem.visible);

      if (props.exparience.webXrOrder?.length > 0) {
        // Fonction pour trier et ajouter les éléments non-triés à la fin
        const matchAndAppend = (raw, sorted) => {
          const sortedMap = new Map(
            sorted
              .map((s) => [s, raw.find((r) => r._id === s)])
              .filter(([_, r]) => r !== undefined)
          );
          const sortedList = Array.from(sortedMap.values());
          const unsortedList = raw.filter((r) => !sortedMap.has(r._id));
          return [...sortedList, ...unsortedList]; // Concaténation des éléments triés et non-triés
        };
        tmp = matchAndAppend(tmp, props.exparience.webXrOrder);
      }

      setActiveItems(tmp);
      setInactiveItems(props.frames.filter((elem) => !elem.visible));
    }
  }, [props.frames]);

  const saveOrder = (orderWithIds) => {
    /// save order
    myExpariencesService
      .saveWebXrOrder(orderWithIds, props.exparience._id, props.isAdmin)
      .then((exp) => {
        const tmpExpObjects = [
          ...props.exparience.objects.filter(
            (obj) => obj.type === "frame" || obj.type === "custom"
          ),
        ].filter((item) => orderWithIds.includes(item._id));
        tmpExpObjects.sort(
          (a, b) => orderWithIds.indexOf(a._id) - orderWithIds.indexOf(b._id)
        );
        props.setVisibleFrames(tmpExpObjects);

        // props.setExparience({
        //   ...props.exparience,
        //   webXrOrder: exp.webXrOrder,
        // });

        setIsUpdatingVisibility(false);
      })
      .catch((err) => {
        console.log("error => ", err);
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.updateWebXrOrderFailed"), {
            variant: "error",
          });
      });
  };

  const MyFrame = ({ frame, invisible = false }) => {
    return (
      <Root>
        <Box
          className={clsx(classes.selectContainer, classes.visibilityLine)}
          sx={{
            maxWidth: !invisible ? "300px" : "inherit",
            marginLeft: "3rem",
          }}
        >
          {!frame.imageUrl && frame.previewUrl ? (
            <img
              alt={`visual-model-visibility-${frame.id}`}
              className={classes.imageExparienceDropdown}
              src={frame.previewUrl}
            />
          ) : !frame.imageUrl ? (
            <Typography variant="body2" className={classes.titleStyle}>
              {frame.link.split("/")[frame.link.split("/").length - 1]}
            </Typography>
          ) : (
            <img
              alt={`visual-frame-visibility-${frame.id}`}
              className={classes.imageExparienceDropdown}
              src={frame.imageUrl}
            />
          )}
          <span className={classes.selectTextVisibility}>
            {frame.title && frame.title.length > 0
              ? frame.title
              : !frame.imageUrl
              ? frame.link.split("/")[frame.link.split("/").length - 1]
              : frame.imageUrl
                  ?.split("/")
                  [frame.imageUrl?.split("/").length - 1].split(".")[0]}
          </span>
          <FormControlLabel
            label={
              frame.visible
                ? t("myDesign.webVR.frameVisible")
                : t("myDesign.webVR.frameInvisible")
            }
            className={classes.visibleSwitch}
            onClick={(e) => e.stopPropagation()}
            control={
              <GoldenSwitch
                checked={frame.visible}
                disabled={isUpdatingVisibility}
                onChange={(e) => {
                  setIsUpdatingVisibility(true);
                  myExpariencesService
                    .updateFrameVisibility(
                      props.exparience._id,
                      frame.id,
                      frame.visible,
                      props.isAdmin
                    )
                    .then((res) => {
                      const tmpExparience = { ...props.exparience };
                      const tmpObjects = [...props.exparience.objects];
                      const tmpIndex = tmpObjects.findIndex(
                        (item) => item.id === frame.id
                      );
                      tmpObjects[tmpIndex] = {
                        ...tmpObjects[tmpIndex],
                        visible: !frame.visible,
                      };
                      tmpExparience.objects = tmpObjects;

                      const tmpActive = [...activeItems];
                      const tmpInactive = [...inactiveItems];
                      if (frame.visible) {
                        const tmp = activeItems.find(
                          (el) => el._id === frame.id
                        );
                        tmpInactive.push({ ...tmp, visible: false });
                        tmpActive.splice(
                          tmpActive.findIndex((el) => el._id === frame.id),
                          1
                        );
                      } else {
                        const tmp = inactiveItems.find(
                          (el) => el._id === frame.id
                        );
                        tmpActive.push({ ...tmp, visible: true });
                        tmpInactive.splice(
                          tmpInactive.findIndex((el) => el._id === frame.id),
                          1
                        );
                      }
                      setInactiveItems(tmpInactive);
                      setActiveItems(tmpActive);
                      const orderWithIds = tmpActive
                        .filter((el) => el._id)
                        .map((el) => el._id);
                      props.setExparience({
                        ...tmpExparience,
                        webXrOrder: orderWithIds,
                      });

                      const tmpFrames = [...props.frames];
                      const frameIndex = props.frames.findIndex(
                        (el) => el._id === frame.id
                      );
                      tmpFrames[frameIndex] = {
                        ...props.frames[frameIndex],
                        visible: !props.frames[frameIndex].visible,
                      };
                      props.setFrames(tmpFrames);

                      saveOrder(orderWithIds);
                    })
                    .catch((err) => {
                      console.log("error +>", e);
                      if (err === 403)
                        enqueueSnackbar(t("toasts.unauthorizedError"), {
                          variant: "error",
                        });
                      else
                        enqueueSnackbar(
                          t("toasts.updateFrameVisibilityError"),
                          {
                            variant: "error",
                          }
                        );
                      setIsUpdatingVisibility(false);
                    });
                }}
                name="isFrameVisible"
                classes={{
                  switchBase: classes.switchBase,
                  checked: classes.checked,
                  track: classes.track,
                }}
              />
            }
          />
        </Box>
      </Root>
    );
  };

  const handleSaveOrderedArray = (newArr) => {
    setActiveItems(newArr);
    props.setExparience({
      ...props.exparience,
      webXrOrder: newArr.filter((el) => el._id).map((el) => el._id),
    });
  };

  if (!inactiveItems || !activeItems) {
    return (
      <Root>
        <div className={classes.loaderContainer}>
          <CircularProgress size={50} />
        </div>
      </Root>
    );
  } else {
    return (
      <Root>
        <div>
          <Typography variant="h5" className={classes.titlesMargin}>
            {t("myExpariences.visibleObjects")}
          </Typography>
          <div className={classes.root}>
            <Paper
              elevation={10}
              sx={{
                padding: "0.25rem",
                background: "#424242",
                maxWidth: "450px",
                width: width > 960 ? "450px" : "350px",
              }}
            >
              <DndKit
                mode={"vertical"}
                customWidth
                data={activeItems}
                reorderedArray={(newArray) => handleSaveOrderedArray(newArray)}
                renderItem={(elem) => (
                  <Paper elevation={10} sx={{ background: "#424242" }}>
                    <MyFrame {...props} frame={elem} />
                  </Paper>
                )}
              />
            </Paper>
          </div>
        </div>
        <Typography variant="h5" className={classes.titlesMargin}>
          {t("myExpariences.invisibleObjects")}
        </Typography>
        <div className={classes.inactiveContainer}>
          {inactiveItems.map((frame) => {
            if (frame.imageUrl || frame.link) {
              return (
                <div
                  key={`visibility-frame-${frame.id}`}
                  className={classes.inactiveFrame}
                >
                  <MyFrame invisible frame={frame} {...props} />
                </div>
              );
            } else {
              return null;
            }
          })}
        </div>
      </Root>
    );
  }
};

export default VisibilityAndOrder;
